import React, { useState, useEffect, useRef } from 'react';
import useApi from '../../hooks/useApi';
import { PRACTICE_INFO } from '../../data/navigation';
import s from './AppointmentForm.module.css';

const SERVICES = [
  'General Checkup & Cleaning', 'Special Care Dentistry', 'Sedation Dentistry',
  'Cosmetic Consultation', 'Orthosnap Aligners', 'GentleWave Root Canal',
  'KöR Whitening', 'Dental Implants', 'Periodontal Treatment', 'Emergency Visit',
];

export default function AppointmentForm({ compact = false }) {
  const { loading, error, success, post, reset } = useApi();

  // ── Anti-bot: record when the form was rendered ──────────
  const formLoadTime = useRef(Date.now());

  const [form, setForm] = useState({
    firstName: '', lastName: '', phone: '', email: '',
    service: '', date: '', isNew: 'Yes — first visit', notes: '',
    // Honeypot field — hidden from real users via CSS, visible to bots
    _hp: '',
  });

  const set = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    if (error) reset();
  };

  // Client-side quick validation before sending
  const isValid = () => {
    if (form.firstName.trim().length < 2) return false;
    if (form.lastName.trim().length < 2) return false;
    if (form.phone.replace(/\D/g, '').length < 10) return false;
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isValid()) return;

    try {
      await post('appointments', {
        ...form,
        // Send load timestamp — server checks that >3 seconds elapsed
        _ts: formLoadTime.current,
      });
    } catch (_) {}
  };

  const minDate = new Date().toISOString().split('T')[0];

  if (success) {
    return (
      <div className={s.success}>
        <div className={s.successIcon}>✅</div>
        <h3>Request Sent!</h3>
        <p>
          Thank you, {form.firstName}! We'll contact you within one business day
          to confirm your appointment.
        </p>
        <a
          href={`tel:${PRACTICE_INFO.phoneRaw}`}
          className="btn btn-outline"
          style={{ marginTop: '16px' }}
        >
          Call {PRACTICE_INFO.phone}
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={s.form} noValidate>
      {!compact && <h3 className={s.formTitle}>Request an Appointment</h3>}
      {!compact && <p className={s.formSub}>We'll confirm within one business day.</p>}

      {/*
        ── HONEYPOT ────────────────────────────────────────────
        This field is visually hidden with CSS (aria-hidden + tabIndex -1).
        Real users never see or fill it.
        Bots fill every input they find — if _hp has any value, the
        server silently drops the submission.
        ────────────────────────────────────────────────────────
      */}
      <div className={s.hpField} aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input
          type="text"
          id="website"
          name="website"
          value={form._hp}
          onChange={set('_hp')}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className={s.row}>
        <div className={s.field}>
          <label htmlFor="firstName">First Name *</label>
          <input
            id="firstName"
            type="text"
            value={form.firstName}
            onChange={set('firstName')}
            placeholder="Jane"
            required
            autoComplete="given-name"
          />
        </div>
        <div className={s.field}>
          <label htmlFor="lastName">Last Name *</label>
          <input
            id="lastName"
            type="text"
            value={form.lastName}
            onChange={set('lastName')}
            placeholder="Smith"
            required
            autoComplete="family-name"
          />
        </div>
      </div>

      <div className={s.row}>
        <div className={s.field}>
          <label htmlFor="phone">Phone *</label>
          <input
            id="phone"
            type="tel"
            value={form.phone}
            onChange={set('phone')}
            placeholder="(215) 000-0000"
            required
            autoComplete="tel"
          />
        </div>
        <div className={s.field}>
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={form.email}
            onChange={set('email')}
            placeholder="jane@email.com"
            autoComplete="email"
          />
        </div>
      </div>

      <div className={s.field}>
        <label htmlFor="service">Service Needed</label>
        <select id="service" value={form.service} onChange={set('service')}>
          <option value="">Select a service…</option>
          {SERVICES.map(sv => <option key={sv} value={sv}>{sv}</option>)}
        </select>
      </div>

      <div className={s.row}>
        <div className={s.field}>
          <label htmlFor="date">Preferred Date</label>
          <input
            id="date"
            type="date"
            value={form.date}
            onChange={set('date')}
            min={minDate}
          />
        </div>
        <div className={s.field}>
          <label htmlFor="isNew">New Patient?</label>
          <select id="isNew" value={form.isNew} onChange={set('isNew')}>
            <option>Yes — first visit</option>
            <option>No — returning patient</option>
          </select>
        </div>
      </div>

      {!compact && (
        <div className={s.field}>
          <label htmlFor="notes">Notes / Questions</label>
          <textarea
            id="notes"
            value={form.notes}
            onChange={set('notes')}
            placeholder="Anything you'd like us to know…"
            rows="3"
          />
        </div>
      )}

      {error && <div className={s.error}>{error}</div>}

      <button type="submit" className={s.submit} disabled={loading || !isValid()}>
        {loading ? 'Sending…' : '📅 Request My Appointment'}
      </button>
    </form>
  );
}
